import { Box, Container, FormControlLabel, makeStyles, MenuItem, Radio, RadioGroup, Select, Slider, Switch } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import clsx from "clsx";
import ColorBox from "../preference/appearance/ColorBox";
import { useRecoilState } from "recoil";
import { audioContextState, coPilotDisplaySettingsSelector } from "../../store/CopilotDisplaySettingsState";
import { deepCopy } from "../../utils/FPEUtils";
import { useState } from "react";
import { ReactComponent as Heli } from '../../images/copilot/heli.svg';
import { ReactComponent as Cessna } from '../../images/copilot/cessna.svg';
import { ReactComponent as Navi } from '../../images/copilot/navi.svg';
import { ReactComponent as OtherCompany } from '../../images/copilot/other.svg';
import { SUBJ_COPI_DISPLAY_SETTINGS, VERB_UPDATE } from "../../utils/AccessControl";
import RoleControl from "../RoleControl";


const useStyles = makeStyles({
  dataList: {
    display: 'flex',
    flexWrap: 'nowrap',
    '& dd:not(:first-child)': {
      marginLeft: 5
    }
  },
  dataListAlignCenter: {
    alignItems: 'center'
  },
  dataLabel: {
    display: 'flex',
    alignItems: 'center',
    width: 200,
    height: 38,
    marginLeft: 10
  },
  blue: {
    color: '#0000FF'
  },
  cyan: {
    color: '#00FFFF'
  },
  deepSkyBlue: {
    color: '#00BFFF'
  },
  green: {
    color: '#008000'
  },
  hotPink: {
    color: '#FF66CC'
  },
  lemonChiffon: {
    color: '#FFFACD'
  },
  lime: {
    color: '#00FF00'
  },
  navy: {
    color: '#000080'
  },
  orange: {
    color: '#FFA500'
  },
  purple: {
    color: '#9933FF'
  },
  red: {
    color: '#FF0000'
  },
  yellow: {
    color: '#FFFF00'
  },
  sliderWidth: {
    width: 170,
    display: 'flex',
    alignItems: 'center'
  },
  sliderSize: {
    marginLeft: 10
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 130
  },
  svgIcon: {
    height: 24,
    width: 24
  },
  iconCircle: {
    height: 20,
    width: 20,
    borderRadius: 50,
    border: "solid 3px"
  },
  fill_blue: {
    fill: '#0000FF'
  },
  fill_cyan: {
    fill: '#00FFFF'
  },
  fill_deepSkyBlue: {
    fill: '#00BFFF'
  },
  fill_green: {
    fill: '#008000'
  },
  fill_hotPink: {
    fill: '#FF66CC'
  },
  fill_lemonChiffon: {
    fill: '#FFFACD'
  },
  fill_lime: {
    fill: '#00FF00'
  },
  fill_navy: {
    fill: '#000080'
  },
  fill_orange: {
    fill: '#FFA500'
  },
  fill_purple: {
    fill: '#9933FF'
  },
  fill_red: {
    fill: '#FF0000'
  },
  fill_yellow: {
    fill: '#FFFF00'
  },
  stroke_blue: {
    stroke: '#0000FF'
  },
  stroke_cyan: {
    stroke: '#00FFFF'
  },
  stroke_deepSkyBlue: {
    stroke: '#00BFFF'
  },
  stroke_green: {
    stroke: '#008000'
  },
  stroke_hotPink: {
    stroke: '#FF66CC'
  },
  stroke_lemonChiffon: {
    stroke: '#FFFACD'
  },
  stroke_lime: {
    stroke: '#00FF00'
  },
  stroke_navy: {
    stroke: '#000080'
  },
  stroke_orange: {
    stroke: '#FFA500'
  },
  stroke_purple: {
    stroke: '#9933FF'
  },
  stroke_red: {
    stroke: '#FF0000'
  },
  stroke_yellow: {
    stroke: '#FFFF00'
  }
});

const ICON_SIZE_MIN = 10;
const ICON_SIZE_MAX = 60;

const LABEL_SIZE_MIN = 10;
const LABEL_SIZE_MAX = 60;

const ICON_COLORS = [
  'red',
  'hotPink',
  'purple',
  'navy',
  'blue',
  'deepSkyBlue',
  'cyan',
  'green',
  'lime',
  'lemonChiffon',
  'yellow',
  'orange'
];

function CoPilotDisplaySetting() {

  const {t} = useTranslation();
  const classes = useStyles();
  const [settings, setSettings] = useRecoilState(coPilotDisplaySettingsSelector);
  const [iconSize, setIconSize] = useState(settings.iconSize);
  const [labelTextSize, setLabelTextSize] = useState(settings.labelTextSize);
  const [ctx, setCtx] = useRecoilState(audioContextState);

  // Icon Color
  const handelIconColorChange = (e) => {
    const {value, name} = e.target;
    setSettings(current => {
      const newData = deepCopy(current);
      newData[name] = value;
      return newData;
    });
  }

  // alert
  const handleAlertSettingChange = (e) => {
    handleChangeState('alertNotification', e);
    if (!ctx) {
      setCtx(new AudioContext());
    }
  }

  // Others
  const handleChangeState = (target, value) => {
    setSettings(current => {
      const newData = deepCopy(current);
      newData[target] = value;
      return newData;
    });
  }

  const handleIconSizeChange = (event, value)  => {
    setIconSize(value);
  }

  const handleLabelTextSizeChange = (event, value) => {
    setLabelTextSize(value);
  }

  return (
    <>
      <Box>
        <ul>
          {/* Icon Color */}
          <li>
            <dl className={classes.dataList}>
              <dt className={classes.dataLabel}>{t('Icon Color')}</dt>
              <dd>
                <ul>
                  {/* Normal time */}
                  <li>
                    <dl className={clsx(classes.dataList, classes.dataListAlignCenter)}>
                      <dt className={classes.dataLabel}>{t('On Normal')}</dt>
                      <dd>
                        <Container className={classes.flexContainer}>
                          {/* Heli */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`heli-icon-normal`}
                                inputProps={{
                                    name: 'heliIconNormal'
                                }}
                                value={settings.heliIconNormal ? settings.heliIconNormal : 'blue'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`heli-icon-normal-${color}`}>
                                      <Heli className={clsx(classes.svgIcon, classes[`fill_${color}`])} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                          {/* Cessna */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`cessna-icon-normal`}
                                inputProps={{
                                    name: 'cessnaIconNormal'
                                }}
                                value={settings.cessnaIconNormal ? settings.cessnaIconNormal : 'blue'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`cessna-icon-normal-${color}`}>
                                      <Cessna className={clsx(classes.svgIcon, classes[`fill_${color}`])} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                          {/* Navi */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`navi-icon-normal`}
                                inputProps={{
                                    name: 'naviIconNormal'
                                }}
                                value={settings.naviIconNormal ? settings.naviIconNormal : 'blue'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`navi-icon-normal-${color}`}>
                                      <Navi className={clsx(classes.svgIcon, classes[`fill_${color}`])} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                          {/* OtherCompany */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`otherCompany-icon-normal`}
                                inputProps={{
                                    name: 'otherCompanyIconNormal'
                                }}
                                value={settings.otherCompanyIconNormal ? settings.otherCompanyIconNormal : 'blue'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`otherCompany-icon-normal-${color}`}>
                                      <OtherCompany className={clsx(classes.svgIcon, classes[`stroke_${color}`])} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                          {/* Other */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`other-icon-normal`}
                                inputProps={{
                                    name: 'otherIconNormal'
                                }}
                                value={settings.otherIconNormal ? settings.otherIconNormal : 'blue'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`other-icon-normal-${color}`}>
                                      <FiberManualRecordIcon className={classes[color]} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                        </Container>
                      </dd>
                    </dl>
                  </li>
                  {/* Alert time */}
                  <li>
                    <dl className={clsx(classes.dataList, classes.dataListAlignCenter)}>
                      <dt className={classes.dataLabel}>{t('On Alert')}</dt>
                      <dd>
                        <Container className={classes.flexContainer}>
                          {/* Heli */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`heli-icon-alert`}
                                inputProps={{
                                    name: 'heliIconAlert'
                                }}
                                value={settings.heliIconAlert ? settings.heliIconAlert : 'red'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`heli-icon-alert-${color}`}>
                                      <Heli className={clsx(classes.svgIcon, classes[`fill_${color}`])} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                          {/* Cessna */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`cessna-icon-alert`}
                                inputProps={{
                                    name: 'cessnaIconAlert'
                                }}
                                value={settings.cessnaIconAlert ? settings.cessnaIconAlert : 'red'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`cessna-icon-alert-${color}`}>
                                      <Cessna className={clsx(classes.svgIcon, classes[`fill_${color}`])} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                          {/* Navi */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`navi-icon-alert`}
                                inputProps={{
                                    name: 'naviIconAlert'
                                }}
                                value={settings.naviIconAlert ? settings.naviIconAlert : 'red'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`navi-icon-alert-${color}`}>
                                      <Navi className={clsx(classes.svgIcon, classes[`fill_${color}`])} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                          {/* OtherCompany */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`otherCompany-icon-alert`}
                                inputProps={{
                                    name: 'otherCompanyIconAlert'
                                }}
                                value={settings.otherCompanyIconAlert ? settings.otherCompanyIconAlert : 'red'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`otherCompany-icon-alert-${color}`}>
                                      <OtherCompany className={clsx(classes.svgIcon, classes[`stroke_${color}`])} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                          {/* Other */}
                          <Container className={classes.flexContainer}>
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <Select
                                id={`other-icon-alert`}
                                inputProps={{
                                    name: 'otherIconAlert'
                                }}
                                value={settings.otherIconAlert ? settings.otherIconAlert : 'red'}
                                onChange={handelIconColorChange}
                              >
                                {
                                  ICON_COLORS.map(color => {
                                    return <MenuItem value={color} key={`other-icon-alert-${color}`}>
                                      <FiberManualRecordIcon className={classes[color]} />
                                    </MenuItem>
                                  })
                                }
                              </Select>
                            </RoleControl>
                          </Container>
                        </Container>
                      </dd>
                    </dl>
                  </li>
                </ul>
              </dd>
            </dl>
          </li>
          {/* Icon Size */}
          <li>
            <dl className={classes.dataList}>
              <dt className={classes.dataLabel}>{t('Icon Size')}</dt>
              <dd>
                <div className={classes.sliderWidth}>
                  <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                    <Slider
                      min={ICON_SIZE_MIN}
                      max={ICON_SIZE_MAX}
                      aria-label='Icon Size'
                      value={iconSize}
                      onChange={handleIconSizeChange}
                      onChangeCommitted={(e, value) => handleChangeState('iconSize', value)}
                      valueLabelDisplay="auto"
                    />
                    <div className={classes.sliderSize}>{iconSize}</div>
                  </RoleControl>
                </div>
              </dd>
            </dl>
          </li>
          {/* Labeling */}
          <li>
            <dl className={classes.dataList}>
              <dt className={classes.dataLabel}>{t('Labeling')}</dt>
              <dd>
                <RadioGroup value={settings.label} onChange={(e) => handleChangeState('label', e.target.value)} row>
                  <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                    <FormControlLabel value="code" control={<Radio />} label={t('Aircraft Code')} />
                    <FormControlLabel value="name" control={<Radio />} label={t('Nickname')} />
                  </RoleControl>
                </RadioGroup>
              </dd>
            </dl>
          </li>
          {/* Label text color */}
          <li>
            <dl className={classes.dataList}>
              <dt className={classes.dataLabel}>{t('Label Text Color')}</dt>
              <dd>
                <ul>
                  <li>
                    <dl className={clsx(classes.dataList, classes.dataListAlignCenter)}>
                      <dt className={classes.dataLabel}></dt>
                      <dd>
                        <Container className={classes.flexContainer}>
                          <Container className={classes.flexContainer}>
                            <Heli className={clsx(classes.svgIcon, classes.fill_blue)} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.heliLabelTextColor ? settings.heliLabelTextColor : '#FFFFFF'} onChange={(color) => handleChangeState('heliLabelTextColor', color)} />
                            </RoleControl>
                          </Container>
                          <Container className={classes.flexContainer}>
                            <Cessna className={clsx(classes.svgIcon, classes.fill_blue)} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.cessnaLabelTextColor ? settings.cessnaLabelTextColor : '#FFFFFF'} onChange={(color) => handleChangeState('cessnaLabelTextColor', color)} />
                            </RoleControl>
                          </Container>
                          <Container className={classes.flexContainer}>
                            <Navi className={clsx(classes.svgIcon, classes.fill_blue)} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.naviLabelTextColor ? settings.naviLabelTextColor : '#FFFFFF'} onChange={(color) => handleChangeState('naviLabelTextColor', color)} />
                            </RoleControl>
                          </Container>
                          <Container className={classes.flexContainer}>
                            <OtherCompany className={clsx(classes.svgIcon, classes.stroke_blue)} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.otherCompanyLabelTextColor ? settings.otherCompanyLabelTextColor : '#FFFFFF'} onChange={(color) => handleChangeState('otherCompanyLabelTextColor', color)} />
                            </RoleControl>
                          </Container>
                          <Container className={classes.flexContainer}>
                            <FiberManualRecordIcon className={classes.blue} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.otherLabelTextColor ? settings.otherLabelTextColor : '#FFFFFF'} onChange={(color) => handleChangeState('otherLabelTextColor', color)} />
                            </RoleControl>
                          </Container>
                        </Container>
                      </dd>
                    </dl>
                  </li>
                </ul>
              </dd>
            </dl>
          </li>
          {/* Label background color */}
          <li>
           <dl className={classes.dataList}>
              <dt className={classes.dataLabel}>{t('Label Background Color')}</dt>
              <dd>
                <ul>
                  <li>
                    <dl className={clsx(classes.dataList, classes.dataListAlignCenter)}>
                      <dt className={classes.dataLabel}></dt>
                      <dd>
                        <Container className={classes.flexContainer}>
                          <Container className={classes.flexContainer}>
                            <Heli className={clsx(classes.svgIcon, classes.fill_blue)} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.heliLabelBGColor ? settings.heliLabelBGColor : 'rgba(0, 0, 255, 0.7)'} onChange={(color) => handleChangeState('heliLabelBGColor', color)} />
                            </RoleControl>
                          </Container>
                          <Container className={classes.flexContainer}>
                            <Cessna className={clsx(classes.svgIcon, classes.fill_blue)} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.cessnaLabelBGColor ? settings.cessnaLabelBGColor : 'rgba(0, 0, 255, 0.7)'} onChange={(color) => handleChangeState('cessnaLabelBGColor', color)} />
                            </RoleControl>
                          </Container>
                          <Container className={classes.flexContainer}>
                            <Navi className={clsx(classes.svgIcon, classes.fill_blue)} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.naviLabelBGColor ? settings.naviLabelBGColor : 'rgba(0, 0, 255, 0.7)'} onChange={(color) => handleChangeState('naviLabelBGColor', color)} />
                            </RoleControl>
                          </Container>
                          <Container className={classes.flexContainer}>
                            <OtherCompany className={clsx(classes.svgIcon, classes.stroke_blue)} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.otherCompanyLabelBGColor ? settings.otherCompanyLabelBGColor : 'rgba(0, 0, 255, 0.7)'} onChange={(color) => handleChangeState('otherCompanyLabelBGColor', color)} />
                            </RoleControl>
                          </Container>
                          <Container className={classes.flexContainer}>
                            <FiberManualRecordIcon className={classes.blue} />
                            <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                              <ColorBox color={settings.otherLabelBGColor ? settings.otherLabelBGColor : 'rgba(0, 0, 255, 0.7)'} onChange={(color) => handleChangeState('otherLabelBGColor', color)} />
                            </RoleControl>
                          </Container>
                        </Container>
                      </dd>
                    </dl>
                  </li>
                </ul>
              </dd>
            </dl>
          </li>
          {/* Label size */}
          <li>
            <dl className={classes.dataList}>
              <dt className={classes.dataLabel}>{t('Label Size')}</dt>
              <dd>
                <div className={classes.sliderWidth}>
                  <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                    <Slider
                      min={LABEL_SIZE_MIN}
                      max={LABEL_SIZE_MAX}
                      aria-label='Label Size'
                      value={labelTextSize}
                      onChange={handleLabelTextSizeChange}
                      onChangeCommitted={(e, value) => handleChangeState('labelTextSize', value)}
                      valueLabelDisplay="auto"
                    />
                    <div className={classes.sliderSize}>{labelTextSize}</div>
                  </RoleControl>
                </div>
              </dd>
            </dl>
          </li>
          {/* List Display */}
          <li>
           <dl className={classes.dataList}>
              <dt className={classes.dataLabel}>{t('List Display')}</dt>
              <dd>
                <RadioGroup value={settings.list} onChange={(e) => handleChangeState('list', e.target.value)} row>
                  <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                    <FormControlLabel value="code" control={<Radio />} label={t('Aircraft Code')} />
                    <FormControlLabel value="name" control={<Radio />} label={t('Nickname')} />
                  </RoleControl>
                </RadioGroup>
              </dd>
            </dl>
          </li>
          {/* Alert sound notification */}
          <li>
           <dl className={classes.dataList}>
              <dt className={classes.dataLabel}>{t('Sound Notification')}</dt>
              <dd>
                <RoleControl subject={SUBJ_COPI_DISPLAY_SETTINGS} verb={VERB_UPDATE}>
                  <Switch
                    checked={settings.alertNotification}
                    onChange={(e) => handleAlertSettingChange(e.target.checked)}
                    color="primary"
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                  />
                </RoleControl>
              </dd>
            </dl>
          </li>
        </ul>
      </Box>
    </>
  )
}

export default CoPilotDisplaySetting;
